import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { GustoMemberData } from '../../types';
import { useContractorPayments, isGustoEnvEnabled } from '../../hooks';
import { formatShortDate, formatCurrency, inputValueToDate } from '../../utils/formatters';

const CONTRACTOR_TYPE = 'CONTRACTOR';
const TEST_ID_PREFIX = 'member-gusto-payments';

interface GustoPaymentHistoryCardProps {
  /** The member's synced Gusto data; used to resolve the contractor UUID/type. */
  data: GustoMemberData | undefined;
}

/**
 * GustoPaymentHistoryCard
 * Read-only card listing a contractor's Gusto payment history. Only renders for
 * CONTRACTOR-type Gusto users with a linked Gusto UUID; other members (e.g.
 * salaried employees) render nothing since the endpoint is contractor-only.
 * The owning card (GustoUserCard) handles admin/manager + connection gating.
 * Payments are paginated (1-based) using the totalPages the backend returns.
 */
export const GustoPaymentHistoryCard: React.FC<GustoPaymentHistoryCardProps> = ({ data }) => {
  const { t } = useTranslation('people');
  const [page, setPage] = useState(1);

  // Build/deploy Gusto kill switch: fully hide payment history when disabled.
  const featureEnabled = isGustoEnvEnabled();
  const gustoUuid = data?.gustoUuid;
  const isContractor = data?.gustoType === CONTRACTOR_TYPE && !!gustoUuid;
  const shouldRender = featureEnabled && isContractor;

  // Reset to the first page whenever the contractor changes.
  useEffect(() => {
    setPage(1);
  }, [gustoUuid]);

  const {
    data: paymentsPage,
    isLoading,
    isFetching,
    error,
  } = useContractorPayments(gustoUuid ?? '', shouldRender, page);

  if (!shouldRender) return null;

  const cardTestId = `${TEST_ID_PREFIX}-card`;

  const formatAmount = (value: string | undefined): string => {
    if (value === undefined || value === null || value === '') return t('detail.gusto.notAvailable');
    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : formatCurrency(parsed);
  };

  const renderShell = (children: React.ReactNode, description?: string) => (
    <Card data-testid={cardTestId}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          <CardTitle>{t('detail.gusto.payments.title')}</CardTitle>
        </div>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );

  if (isLoading) {
    return renderShell(
      <div className="flex items-center justify-center py-8">
        <Loader2
          className="h-6 w-6 animate-spin text-muted-foreground"
          data-testid={`${cardTestId}-loading`}
        />
      </div>
    );
  }

  if (error) {
    return renderShell(
      <div className="py-8 text-center space-y-1" data-testid={`${cardTestId}-error`}>
        <p className="text-sm font-medium">{t('detail.gusto.payments.error')}</p>
      </div>,
      t('detail.gusto.payments.description')
    );
  }

  const payments = paymentsPage?.payments ?? [];

  if (payments.length === 0) {
    return renderShell(
      <div className="py-8 text-center space-y-1" data-testid={`${cardTestId}-empty`}>
        <p className="text-sm font-medium">{t('detail.gusto.payments.empty')}</p>
      </div>,
      t('detail.gusto.payments.description')
    );
  }

  const na = t('detail.gusto.notAvailable');
  const currentPage = paymentsPage?.page ?? page;
  const totalPages = paymentsPage?.totalPages ?? 1;
  const canPrev = currentPage > 1 && !isFetching;
  const canNext = currentPage < totalPages && !isFetching;

  return renderShell(
    <>
      <Table data-testid={`${TEST_ID_PREFIX}-table`}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('detail.gusto.payments.columns.date')}</TableHead>
            <TableHead>{t('detail.gusto.payments.columns.wage')}</TableHead>
            <TableHead>{t('detail.gusto.payments.columns.bonus')}</TableHead>
            <TableHead>{t('detail.gusto.payments.columns.reimbursement')}</TableHead>
            <TableHead>{t('detail.gusto.payments.columns.total')}</TableHead>
            <TableHead>{t('detail.gusto.payments.columns.method')}</TableHead>
            <TableHead>{t('detail.gusto.payments.columns.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.map((payment, index) => (
            <TableRow
              key={payment.paymentUuid ?? index}
              data-testid={`${TEST_ID_PREFIX}-row`}
            >
              <TableCell>
                {payment.date
                  ? formatShortDate(inputValueToDate(payment.date) ?? payment.date)
                  : na}
              </TableCell>
              <TableCell>{formatAmount(payment.wage)}</TableCell>
              <TableCell>{formatAmount(payment.bonus)}</TableCell>
              <TableCell>{formatAmount(payment.reimbursement)}</TableCell>
              <TableCell className="font-medium">{formatAmount(payment.wageTotal)}</TableCell>
              <TableCell>{payment.paymentMethod || na}</TableCell>
              <TableCell>{payment.status || na}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div
          className="flex items-center justify-between pt-4"
          data-testid={`${TEST_ID_PREFIX}-pagination`}
        >
          <p className="text-sm text-muted-foreground" data-testid={`${TEST_ID_PREFIX}-page-info`}>
            {t('detail.gusto.payments.pagination.pageOf', { page: currentPage, total: totalPages })}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canPrev}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid={`${TEST_ID_PREFIX}-prev`}
            >
              <ChevronLeft className="h-4 w-4" />
              {t('detail.gusto.payments.pagination.previous')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canNext}
              onClick={() => setPage((p) => p + 1)}
              data-testid={`${TEST_ID_PREFIX}-next`}
            >
              {t('detail.gusto.payments.pagination.next')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>,
    t('detail.gusto.payments.description')
  );
};
